import {
  AccessTime,
  Cancel,
  CheckCircle,
  OutdoorGrill,
  Replay,
  Restaurant,
  ShoppingBag,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  keyframes,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { PortalTheme } from '../../theme';

export type TransactionStatusType =
  | 'received'
  | 'approved'
  | 'ready'
  | 'delivered'
  | 'cancelled'
  | 'refunded';

export interface TransactionStatusCardProps {
  status: TransactionStatusType;
  title: string;
  description: string;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const STATUSES_WITH_SPINNER: TransactionStatusType[] = ['approved', 'ready'];

const ICON_SIZE = { fontSize: 32 };

const STATUS_ICONS: Record<TransactionStatusType, React.ReactNode> = {
  received: (
    <AccessTime sx={{ ...ICON_SIZE, color: 'white' }} aria-hidden="true" />
  ),
  approved: (
    <OutdoorGrill sx={{ ...ICON_SIZE, color: 'white' }} aria-hidden="true" />
  ),
  ready: (
    <ShoppingBag sx={{ ...ICON_SIZE, color: 'white' }} aria-hidden="true" />
  ),
  delivered: (
    <Restaurant sx={{ ...ICON_SIZE, color: 'white' }} aria-hidden="true" />
  ),
  cancelled: (
    <Cancel sx={{ ...ICON_SIZE, color: 'white' }} aria-hidden="true" />
  ),
  refunded: <Replay sx={{ ...ICON_SIZE, color: 'white' }} aria-hidden="true" />,
};

export const TransactionStatusCard = (props: TransactionStatusCardProps) => {
  const theme = useTheme<PortalTheme>();

  const statusColors = theme.palette.transactionStatus?.[props.status];
  const backgroundColor = statusColors?.background;
  const iconCircleColor = statusColors?.icon;
  const showSpinner = STATUSES_WITH_SPINNER.includes(props.status);

  return (
    <Card
      role="status"
      aria-label={props.title}
      sx={{
        width: '100%',
        backgroundColor,
        border: 'none',
        borderTopLeftRadius: `0px`,
        borderTopRightRadius: `0px`,
        borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
        borderBottomRightRadius: `${theme.shape.borderRadius}px`,
        boxShadow: theme.boxShadow,
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          py: 4,
          ':last-child': { pb: 4 },
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          {showSpinner && (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                top: -6,
                left: -6,
                width: 84,
                height: 84,
                borderRadius: '50%',
                border: '3px solid transparent',
                borderTopColor: iconCircleColor,
                animation: `${spin} 1.5s linear infinite`,
              }}
            />
          )}
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: iconCircleColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {STATUS_ICONS[props.status]}
          </Box>
          {props.status === 'delivered' && (
            <CheckCircle
              aria-hidden="true"
              sx={{
                position: 'absolute',
                bottom: 0,
                right: -2,
                color: 'success.main',
                backgroundColor: backgroundColor,
                borderRadius: '50%',
                fontSize: 22,
              }}
            />
          )}
        </Box>
        <Stack spacing={0.5} alignItems="center" sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle1" color="text.primary">
            {props.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {props.description}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};
